import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    Chip, CircularProgress, FormControl, InputLabel, Select, MenuItem, IconButton,
    FormControlLabel, Switch, Autocomplete, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/v2Api';
import { toImportPayload, chapterLabel, contentKey, courseGroup, sortCoursesForPicker, kindWord, clusterByGroup } from './importUtils';

// Recipe-based random test generator: the admin lists chapters + how many
// questions to pull from each + which section each pull lands in; Generate
// randomly samples from the chapter pools and hands editor-ready sections
// back to AddEditTest. Everything after that is normal manual editing.

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// chapterIdTo = '' means a single chapter; set, it makes the row a RANGE and
// the pool becomes the union of every chapter from chapterId..chapterIdTo.
const emptyRow = (section = 1) => ({ course: '', chapterId: '', chapterIdTo: '', count: 5, section });

// existingSections: the test's current sections (ordered, {name}) — the Section
// dropdown targets these by number; numbers past the end create new sections.
export default function GenerateFromChaptersModal({ open, onClose, onGenerate, existingSections = [] }) {
    const [courses, setCourses] = useState([]);
    const [chaptersByCourse, setChaptersByCourse] = useState({}); // course -> chapters[]
    const [pools, setPools] = useState({}); // chapterId -> {chapter, questions}
    const [loadingPool, setLoadingPool] = useState(0); // count of in-flight pool fetches
    const [rows, setRows] = useState([emptyRow()]);
    // Pools default to source tests only (test_category='source'); this switch
    // widens them to every test — pools refetch when it changes.
    const [includeAll, setIncludeAll] = useState(false);

    useEffect(() => {
        if (!open) return;
        setRows([emptyRow()]);
        setPools({});
        setIncludeAll(false);
        api.get('/chapters/courses').then((r) => setCourses(r.data.data || [])).catch(() => {});
    }, [open]);

    const loadChapters = (course) => {
        if (chaptersByCourse[course]) return;
        api.get('/chapters', { course })
            .then((r) => setChaptersByCourse((prev) => ({ ...prev, [course]: r.data.data || [] })))
            .catch(() => {});
    };

    const fetchPool = (chapterId, all) => {
        setLoadingPool((n) => n + 1);
        api.get(`/chapters/${chapterId}/questions`, all ? { include_all: 1 } : {})
            .then((r) => setPools((prev) => ({ ...prev, [chapterId]: r.data.data })))
            .catch(() => {})
            .finally(() => setLoadingPool((n) => n - 1));
    };

    const loadPool = (chapterId) => {
        if (!chapterId || pools[chapterId]) return;
        fetchPool(chapterId, includeAll);
    };

    const chaptersOf = (course) => [...(chaptersByCourse[course] || [])].sort((a, b) => a.number - b.number);

    // Chapter ids a row draws from: [chapterId] alone, or every chapter whose
    // number falls inside chapterId..chapterIdTo (inclusive, either order).
    const rangeIds = (row) => {
        if (!row.chapterId) return [];
        if (!row.chapterIdTo) return [row.chapterId];
        const chs = chaptersOf(row.course);
        const a = chs.find((c) => c.id === row.chapterId);
        const b = chs.find((c) => c.id === row.chapterIdTo);
        if (!a || !b) return [row.chapterId];
        const [lo, hi] = a.number <= b.number ? [a.number, b.number] : [b.number, a.number];
        return chs.filter((c) => c.number >= lo && c.number <= hi).map((c) => c.id);
    };

    // Flipping the source filter invalidates every loaded pool — refetch the
    // chapters currently in use with the new filter.
    const toggleIncludeAll = (all) => {
        setIncludeAll(all);
        setPools({});
        [...new Set(rows.flatMap((r) => rangeIds(r)))].forEach((id) => fetchPool(id, all));
    };

    const updateRow = (i, patch) => setRows((prev) => prev.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
    const removeRow = (i) => setRows((prev) => prev.filter((_, ri) => ri !== i));
    const existingCount = existingSections.length;
    const maxSection = Math.max(1, existingCount, ...rows.map((r) => r.section));
    const sectionLabel = (n) => {
        if (n <= existingCount) {
            const name = existingSections[n - 1]?.name;
            return `Section ${n}${name && name !== `Section ${n}` ? ` — ${name}` : ''}`;
        }
        return `Section ${n} (new)`;
    };

    // Per-section plan with per-row clamping to the available pool (dedup-aware:
    // overlapping rows/ranges can't take the same question twice — remaining
    // capacity is tracked per chapter and allocated greedily across a range).
    const plan = useMemo(() => {
        const bySection = new Map();
        const taken = {};
        for (const r of rows) {
            const ids = rangeIds(r);
            if (!ids.length) continue;
            const loaded = ids.every((id) => pools[id]);
            let take = r.count, available = null;
            if (loaded) {
                available = ids.reduce((n, id) => n + Math.max(0, pools[id].questions.length - (taken[id] || 0)), 0);
                take = Math.max(0, Math.min(r.count, available));
                let left = take;
                for (const id of ids) {
                    if (!left) break;
                    const grab = Math.min(pools[id].questions.length - (taken[id] || 0), left);
                    taken[id] = (taken[id] || 0) + grab;
                    left -= grab;
                }
            }
            if (!bySection.has(r.section)) bySection.set(r.section, []);
            bySection.get(r.section).push({
                row: r, ids, take, available,
                chapters: ids.map((id) => pools[id]?.chapter).filter(Boolean),
            });
        }
        return [...bySection.entries()].sort((a, b) => a[0] - b[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, pools, chaptersByCourse]);

    const totalQuestions = plan.reduce((n, [, items]) => n + items.reduce((m, it) => m + it.take, 0), 0);
    const ready = totalQuestions > 0 && loadingPool === 0 && rows.every((r) => rangeIds(r).every((id) => pools[id]));

    const handleGenerate = () => {
        // Dedup by row id AND by content: the same question can exist as copies in
        // several tests (e.g. vocab items embedded in English exams) — never pull
        // identical text twice in one run.
        const usedIds = new Set();
        const usedContent = new Set();
        const numbered = plan.map(([sectionNo, items]) => {
            const payloads = [];
            for (const it of items) {
                // union pool across the row's range; each question keeps its own chapter
                const candidates = [];
                for (const id of it.ids) {
                    const pool = pools[id];
                    if (pool) for (const q of pool.questions) candidates.push({ q, pool });
                }
                let picked = 0;
                for (const { q, pool } of shuffle(candidates)) {
                    if (picked >= it.take) break;
                    if (usedIds.has(q.id)) continue;
                    const ck = contentKey(q);
                    if (ck && usedContent.has(ck)) continue;
                    usedIds.add(q.id);
                    if (ck) usedContent.add(ck);
                    payloads.push(toImportPayload(
                        { ...q, v2_chapter: q.v2_chapter || pool.chapter },
                        q.v2_test || null,
                        q.v2_question_group || null
                    ));
                    picked++;
                }
            }
            // random pick order, but passage-mates end up adjacent
            return { sectionNumber: sectionNo, payloads: clusterByGroup(payloads) };
        }).filter((s) => s.payloads.length > 0);
        onGenerate(numbered);
    };

    const chipLabel = (it) => {
        const chs = it.chapters;
        if (!chs.length) return `… ×${it.take}`;
        const label = chs.length > 1
            ? (chs[0].kind === 'difficulty'
                ? `${chapterLabel(chs[0])}–${chapterLabel(chs[chs.length - 1])}`
                : `${chapterLabel(chs[0])}–${chs[chs.length - 1].number}`)
            : chapterLabel(chs[0]);
        return `${chs[0].course} · ${label} ×${it.take}`;
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Generate from Chapters
                <Typography variant="body2" color="text.secondary">
                    Pick chapters and how many questions to pull from each — Generate draws them at random.
                    You can fine-tune everything in the editor afterwards.
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={1.25}>
                    {rows.map((row, i) => {
                        const chapters = chaptersOf(row.course);
                        const ids = rangeIds(row);
                        const loaded = ids.length > 0 && ids.every((id) => pools[id]);
                        const rowAvail = loaded ? ids.reduce((n, id) => n + pools[id].questions.length, 0) : null;
                        const startCh = chapters.find((c) => c.id === row.chapterId);
                        return (
                            <Box key={i} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                <Autocomplete
                                    size="small" sx={{ minWidth: 200 }}
                                    options={sortCoursesForPicker([...new Set(courses.map((c) => c.course))])}
                                    groupBy={courseGroup}
                                    value={row.course || null}
                                    onChange={(e, c) => {
                                        updateRow(i, { course: c || '', chapterId: '', chapterIdTo: '' });
                                        if (c) loadChapters(c);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Course" />}
                                />
                                <Autocomplete
                                    size="small" sx={{ minWidth: 150 }} disabled={!row.course}
                                    options={chapters}
                                    getOptionLabel={(ch) => `${chapterLabel(ch)}${ch.label && ch.kind !== 'difficulty' ? ` — ${ch.label}` : ''}`}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    value={chapters.find((c) => c.id === row.chapterId) || null}
                                    onChange={(e, ch) => {
                                        updateRow(i, { chapterId: ch ? ch.id : '', chapterIdTo: '' });
                                        if (ch) loadPool(ch.id);
                                    }}
                                    renderInput={(params) => <TextField {...params} label={kindWord(courses.find((c) => c.course === row.course)?.kind)} />}
                                />
                                <Autocomplete
                                    size="small" sx={{ minWidth: 130 }} disabled={!row.chapterId}
                                    options={chapters.filter((ch) => startCh && ch.number > startCh.number)}
                                    getOptionLabel={(ch) => chapterLabel(ch)}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    value={chapters.find((c) => c.id === row.chapterIdTo) || null}
                                    onChange={(e, ch) => {
                                        const v = ch ? ch.id : '';
                                        updateRow(i, { chapterIdTo: v });
                                        if (v) rangeIds({ ...row, chapterIdTo: v }).forEach(loadPool);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="to (range)" placeholder="single" />}
                                />
                                <Box component="input" type="number" min="1" max={rowAvail ?? undefined} value={row.count}
                                    onChange={(e) => {
                                        let v = Math.max(1, parseInt(e.target.value) || 1);
                                        if (rowAvail != null) v = Math.min(v, rowAvail); // capped to the range pool
                                        updateRow(i, { count: v });
                                    }}
                                    style={{ width: 58, padding: '8px 6px', textAlign: 'center', borderRadius: 4, border: '1px solid rgba(128,128,128,.4)', background: 'transparent', color: 'inherit' }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 86 }}>
                                    {row.chapterId ? (loaded ? `of ${rowAvail} available` : <CircularProgress size={12} />) : 'questions'}
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>Section</InputLabel>
                                    <Select value={row.section} label="Section" onChange={(e) => updateRow(i, { section: e.target.value })}>
                                        {Array.from({ length: maxSection + 1 }, (_, k) => k + 1).map((n) => (
                                            <MenuItem key={n} value={n}>{sectionLabel(n)}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <IconButton size="small" onClick={() => removeRow(i)} disabled={rows.length === 1} sx={{ color: 'error.main' }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
                    <Button startIcon={<AddIcon />} size="small"
                        onClick={() => setRows((prev) => [...prev, emptyRow(prev[prev.length - 1]?.section || 1)])}>
                        Add Chapter
                    </Button>
                    <FormControlLabel
                        sx={{ mr: 0 }}
                        control={<Switch size="small" checked={includeAll} onChange={(e) => toggleIncludeAll(e.target.checked)} />}
                        label={<Typography variant="caption">Include non-source tests (exam copies)</Typography>}
                    />
                </Box>
            </DialogContent>
            {/* Live plan — what Generate will create */}
            {totalQuestions > 0 && (
                <Box sx={{ px: 3, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary">Will create:</Typography>
                    {plan.map(([sectionNo, items]) => (
                        <Chip key={sectionNo} size="small" variant="outlined"
                            sx={{ height: 22, fontSize: 11, color: 'text.primary', borderColor: 'text.secondary' }}
                            label={`${sectionLabel(sectionNo)}: ${items.map(chipLabel).join(', ')}`} />
                    ))}
                    <Typography variant="caption" color="text.secondary">= {totalQuestions} questions</Typography>
                </Box>
            )}
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="secondary" disabled={!ready} onClick={handleGenerate}>
                    Generate {totalQuestions > 0 ? totalQuestions : ''} Questions
                </Button>
            </DialogActions>
        </Dialog>
    );
}
